import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowUpRight,
  Zap, Compass, Tag, Smartphone,
  Hand, Brain, Play, Dices, ChevronRight,
  Repeat, Puzzle, Hash, MousePointerClick, Smartphone as Phone,
  Film, Gift, Layers, TrendingUp,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useReveal from '../hooks/useReveal';
import { projects } from '../mock';
import byte1Img from '../assets/specials/byte1.png';
import byte2Img from '../assets/specials/byte2.png';
import recc1Img from '../assets/specials/recc1.png';
import recc2Img from '../assets/specials/recc2.png';
import vibeQuizImg from '../assets/specials/vibe-quiz.png';
import genreImg from '../assets/specials/genre.png';
import tpSpecialsImg from '../assets/specials/tp-specials.png';
import categoryImg from '../assets/specials/category.png';
import filterImg from '../assets/specials/filter.png';
import rechargeRewardImg from '../assets/specials/recharge-reward.png';
import special1Img from '../assets/specials/special1.png';
import special2Img from '../assets/specials/special2.png';

const FIGMA_BYTES_VIBE = 'https://www.figma.com/proto/yH0rYZHb82BrQzzuPcYJmr/%E2%9C%85-TPMA-Designs--June-25---Jan--26-?node-id=1544-28776&viewport=29%2C2946%2C0.28&t=0xsEbse5RFpk6dSX-1&scaling=min-zoom&content-scaling=fixed&starting-point-node-id=1544%3A28776&show-proto-sidebar=1&page-id=0%3A1';
const FIGMA_SHAKE      = 'https://www.figma.com/proto/yH0rYZHb82BrQzzuPcYJmr/%E2%9C%85-TPMA-Designs--June-25---Jan--26-?node-id=153-14301&viewport=29%2C2946%2C0.28&t=0xsEbse5RFpk6dSX-1&scaling=min-zoom&content-scaling=fixed&starting-point-node-id=153%3A14301&show-proto-sidebar=1&page-id=0%3A1';

const SECTIONS = ['hero', 'problem', 'surface', 'interaction', 'genre', 'engine', 'scale'];
const SECTION_LABELS = ['Intro', 'Problem', 'Reframe', 'Interactions', 'Genre', 'Last stop', 'Scale'];

export default function SpecialsCaseStudy() {
  useReveal();
  const project = useMemo(() => projects.find((p) => p.slug === 'specials-destination-page'), []);
  const nextProject = useMemo(() => {
    const idx = projects.findIndex((p) => p.slug === 'specials-destination-page');
    return projects[(idx + 1) % projects.length];
  }, []);

  const [progress, setProgress] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [byteIdx, setByteIdx] = useState(0);
  const [reccIdx, setReccIdx] = useState(0);
  const [endIdx, setEndIdx] = useState(0);
  const [libReccIdx, setLibReccIdx] = useState(0);
  const [getSpecialIdx, setGetSpecialIdx] = useState(0);
  const byteResetRef = useRef(null);
  const reccResetRef = useRef(null);
  const endResetRef = useRef(null);
  const libReccResetRef = useRef(null);
  const getSpecialResetRef = useRef(null);

  // Click image to advance, auto-reset after 2 min idle
  const advanceByte = () => {
    setByteIdx((i) => (i === 0 ? 1 : 0));
    if (byteResetRef.current) clearTimeout(byteResetRef.current);
    byteResetRef.current = setTimeout(() => setByteIdx(0), 120000);
  };
  const advanceRecc = () => {
    setReccIdx((i) => (i === 0 ? 1 : 0));
    if (reccResetRef.current) clearTimeout(reccResetRef.current);
    reccResetRef.current = setTimeout(() => setReccIdx(0), 120000);
  };
  const advanceLibRecc = () => {
    setLibReccIdx((i) => (i === 0 ? 1 : 0));
    if (libReccResetRef.current) clearTimeout(libReccResetRef.current);
    libReccResetRef.current = setTimeout(() => setLibReccIdx(0), 120000);
  };
  const advanceGetSpecial = () => {
    setGetSpecialIdx((i) => (i === 0 ? 1 : 0));
    if (getSpecialResetRef.current) clearTimeout(getSpecialResetRef.current);
    getSpecialResetRef.current = setTimeout(() => setGetSpecialIdx(0), 120000);
  };
  const advanceEnd = () => {
    setEndIdx((i) => (i + 1) % 3);
    if (endResetRef.current) clearTimeout(endResetRef.current);
    endResetRef.current = setTimeout(() => setEndIdx(0), 120000);
  };

  useEffect(() => () => {
    if (byteResetRef.current) clearTimeout(byteResetRef.current);
    if (reccResetRef.current) clearTimeout(reccResetRef.current);
    if (endResetRef.current) clearTimeout(endResetRef.current);
    if (libReccResetRef.current) clearTimeout(libReccResetRef.current);
    if (getSpecialResetRef.current) clearTimeout(getSpecialResetRef.current);
  }, []);

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);
  useEffect(() => {
    const onScroll = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, Math.round((window.scrollY / total) * 100)) : 0);
      let active = 0;
      SECTIONS.forEach((id, i) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < window.innerHeight * 0.5) active = i;
      });
      setActiveIdx(active);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (i) => {
    const el = document.getElementById(SECTIONS[i]);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!project) return null;

  return (
    <div className="App">
      <Navbar />

      <div className="fixed top-0 left-0 h-[3px] z-[60] transition-[width] duration-100" style={{ width: `${progress}%`, background: 'var(--terracotta)' }} />

      <div className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col gap-3">
        {SECTIONS.map((_, i) => (
          <button key={i} onClick={() => scrollTo(i)} title={SECTION_LABELS[i]} className="group flex items-center gap-3" aria-label={SECTION_LABELS[i]}>
            <span className="text-[10px] tracking-[0.18em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ color: 'var(--ink-soft)' }}>
              {SECTION_LABELS[i]}
            </span>
            <span className="rounded-full transition-all duration-300 block" style={{ width: activeIdx === i ? 12 : 8, height: activeIdx === i ? 12 : 8, background: activeIdx === i ? 'var(--terracotta)' : 'var(--border)' }} />
          </button>
        ))}
      </div>

      <article className="pt-28 md:pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/#work" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase" style={{ color: 'var(--terracotta-deep)' }}>
            <ArrowLeft size={14} /> <span className="link-underline">Back to Work</span>
          </Link>
        </div>

        {/* HERO */}
        <section id="hero" className="max-w-3xl mx-auto px-6 pt-12 pb-16 mb-12" style={{ borderBottom: '1px solid var(--border)' }}>
          <p className="reveal text-[11px] tracking-[0.22em] uppercase mb-5" style={{ color: 'var(--terracotta)' }}>
            Case study · Tata Play · Specials
          </p>
          <h1 className="reveal reveal-delay-1 font-display leading-[1.05] mb-6" style={{ fontSize: 'clamp(40px, 6vw, 64px)', color: 'var(--ink)', fontWeight: 600 }}>
            Giving curated content packs a place to <em className="italic" style={{ color: 'var(--terracotta)' }}>be discovered</em>
          </h1>
          <p className="reveal reveal-delay-2 text-[16px] leading-[1.75] mb-8 max-w-[580px]" style={{ color: 'var(--ink-soft)' }}>
            Tata Play Specials had real value — curated content packs available to buy across the app. What it never had was a home.
            Specials lived buried inside other journeys, easy to miss and easier to forget. There was no dedicated destination for it.
            I designed one from scratch.
          </p>
          <div className="reveal reveal-delay-3 flex flex-wrap gap-2.5 mb-5">
            <MetaPill accent>Assistant Manager - UI/UX · End-to-end</MetaPill>
            <MetaPill>Strategy · Interaction · IA</MetaPill>
            <MetaPill>10+ custom widgets</MetaPill>
          </div>
          <p className="reveal reveal-delay-3 text-[12.5px] tracking-[0.08em]" style={{ color: 'var(--ink-soft)' }}>
            <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Focus:</strong> Content design · Discovery · Modular widgets
          </p>
        </section>

        {/* Cover gradient */}
        <div className="max-w-6xl mx-auto px-6 mb-16">
          <div className="reveal rounded-[22px] overflow-hidden shadow-warm-lg relative" style={{ aspectRatio: '2/1', background: project.gradient }}>
            {project.cover && <img src={project.cover} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />}
          </div>
        </div>

        {/* 01 — PROBLEM */}
        <Chapter id="problem" number="01 — Where Specials actually lived">
          <H2>Buried, not broken. <em className="italic" style={{ color: 'var(--terracotta)' }}>Just never given a home.</em></H2>
          <P>
            Before this project, there was no Specials destination page at all. The packs existed and people did buy them — but only when
            they happened to run into a banner, a stray carousel card, or a promo tucked inside an unrelated journey in TPMA. Nobody opened
            the app looking for Specials, because there was never anywhere to go looking.
          </P>

          {/* Before / After — full width, stacked */}
          <div className="reveal flex flex-col gap-3.5 my-8">
            <BACard variant="before" label="Before" Icon={Layers}>
              Specials scattered across banners, promos, and one-off carousels inside other journeys.
            </BACard>
            <div className="flex justify-center" aria-hidden="true">
              <ArrowRight size={20} className="rotate-90" style={{ color: 'var(--terracotta)' }} />
            </div>
            <BACard variant="after" label="What I built" Icon={Compass}>
              A dedicated destination page, designed from scratch, purpose-built for discovery.
            </BACard>
          </div>

          <P>
            Once the destination existed as a concept, the actual problem came into focus. It wasn&apos;t that the content lacked value — it
            was missing the three things any new destination needs: a clear entry point, a reason to explore once you arrived, and momentum
            to keep going.
          </P>

          <InsightBlock Icon={Zap}>
            When discovery feels like effort, people don&apos;t push through it. They fall back to what they already know — for most users,
            that meant Specials simply never entered the picture.
          </InsightBlock>

          {/* Wrong / Right question */}
          <div className="reveal grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3.5 items-stretch my-7">
            <ShiftCard tone="wrong" label="Wrong question">&ldquo;How do we show more content?&rdquo;</ShiftCard>
            <div className="hidden md:flex items-center justify-center" style={{ color: 'var(--terracotta)' }}><ArrowRight size={22} /></div>
            <div className="flex md:hidden items-center justify-center" style={{ color: 'var(--terracotta)' }}><ArrowRight size={22} className="rotate-90" /></div>
            <ShiftCard tone="right" label="Right question">&ldquo;How do we make exploring feel effortless — maybe even fun?&rdquo;</ShiftCard>
          </div>
        </Chapter>

        <Ornament />

        {/* 02 — REFRAMING */}
        <Chapter id="surface" number="02 — Reframing the page">
          <H2>Not a catalogue. <em className="italic" style={{ color: 'var(--terracotta)' }}>A discovery surface.</em></H2>
          <P>
            A traditional catalogue would have been the safe answer — organise everything, let people scroll, done. But catalogues are built
            to organise content, not to spark curiosity. With no existing destination to anchor against, I had a genuinely blank canvas, and
            two very different ways to fill it.</P>

          {/* Two approaches — equal weightage */}
          <div className="reveal grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
            <ApproachCard
              accent="terracotta"
              label="Approach A"
              Icon={Tag}
              title="Specials-first"
              body="Treat Specials as the product itself. Lead with category structure, surface subscription value upfront, and design the whole page around driving the purchase decision directly."
            />
            <ApproachCard
              accent="terracotta"
              label="Approach B"
              Icon={Film}
              title="Content-first"
              body="Let people start with what they already want — shows, movies, previews — and let the fact that it's a Special reveal itself organically as they browse, rather than leading with the business label."
            />
          </div>

          <P>
            What this exercise clarified: people don&apos;t care about &ldquo;Specials&rdquo; as a category. They care about what looks
            interesting, what feels relevant right now, and what&apos;s easy enough to just try. That pointed toward content-first as the
            stronger foundation — but the final destination still needed Specials-first moments layered in, particularly anywhere a purchase
            decision needed to happen.
          </P>
        </Chapter>

        <Ornament />

        {/* 03 — INTERACTIONS */}
        <Chapter id="interaction" number="03 — Designing for interaction, not navigation">
          <H2>Stop stacking rails. <em className="italic" style={{ color: 'var(--terracotta)' }}>Start designing moments.</em></H2>
          <P>
            This is where the destination actually took shape. Instead of adding another row of tiles, I introduced a handful of components
            built around doing something, not just looking at something. Below are three of those moments — the full motion versions live as
            prototypes linked below.
          </P>

          <WidgetBlock
            n="Widget 01"
            title="Special Bytes"
            Icon={Play}
            sub="Story-style previews that ask nothing of you upfront. Tap one and you get a few seconds of a show before committing to anything — the same low-effort, high-curiosity shape as a social story."
            images={[byte1Img, byte2Img]}
            activeIdx={byteIdx}
            onTap={advanceByte}
          />

          <WidgetBlock
            n="Widget 02"
            title="Recommendation of the Week"
            Icon={Dices}
            sub="A shake reveals it, not a tap — breaking passive scrolling on purpose with a small bit of physical surprise instead of another button."
            images={[recc1Img, recc2Img]}
            activeIdx={reccIdx}
            onTap={advanceRecc}
          />

          <WidgetBlock
            n="Widget 03"
            title={'\u201CWhat\u2019s your vibe?\u201D quiz'}
            Icon={Brain}
            sub="For people who don't know what they want, two quick taps replace an empty search box — removing decision fatigue while still feeling personal, not heavy."
            images={[vibeQuizImg]}
          />

          <p className="text-[12.5px] mt-7 mb-3 italic" style={{ color: 'var(--ink-soft)' }}>
            This case study can&apos;t hold video, so the originals — built with motion and GIF-led headers — live as real prototypes instead.
            Tap below to see the actual designs.
          </p>
          <div className="reveal flex flex-wrap gap-2.5">
            <ProtoButton href={FIGMA_BYTES_VIBE}>Special Bytes prototype</ProtoButton>
            <ProtoButton href={FIGMA_SHAKE}>Shake-to-reveal prototype</ProtoButton>
            <ProtoButton href={FIGMA_BYTES_VIBE}>Vibe quiz prototype</ProtoButton>
          </div>
        </Chapter>

        <Ornament />

        {/* 04 — GENRE PAGE */}
        <Chapter id="genre" number="04 — The genre page">
          <H2>One more layer, <em className="italic" style={{ color: 'var(--terracotta)' }}>for the still-undecided.</em></H2>
          <P>
            Tapping any genre tab in the genre rail on the destination page opens a dedicated genre page — not just a filtered version of
            the same rail. The genre you tapped arrives pre-selected in the filter, and a language filter sits alongside it, so the narrowing
            carries through instead of resetting.
          </P>

          <IllustrationBox icon={Smartphone} label="Genre page — full screen reference">
            <ScreenPhone src={genreImg} alt="Genre page — Categories + Languages filters with Tamil/Marathi Classics" />
          </IllustrationBox>
        </Chapter>

        <Ornament />

        {/* 05 — LAST STOP */}
        <Chapter id="engine" number="05 — The last stop on the page">
          <H2>For the people who scroll all the way down. <em className="italic" style={{ color: 'var(--terracotta)' }}>Still undecided.</em></H2>
          <P>
            Playful moments solve &ldquo;I don&apos;t want to browse.&rdquo; They don&apos;t solve &ldquo;I genuinely don&apos;t know what to
            watch.&rdquo; That second problem needed something deliberately placed at the very end of the destination page — a last, structured
            attempt to help, for anyone who&apos;d made it all the way down without finding something.
          </P>

          {/* Two-step flow */}
          <div className="reveal grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3.5 items-stretch my-7">
            <StepCard label="Step 1">Pick a genre or category</StepCard>
            <div className="hidden md:flex items-center justify-center" style={{ color: 'var(--terracotta)' }}><ArrowRight size={22} /></div>
            <div className="flex md:hidden items-center justify-center" style={{ color: 'var(--terracotta)' }}><ArrowRight size={22} className="rotate-90" /></div>
            <StepCard label="Step 2">Cards surface, with language filter on top</StepCard>
          </div>

          <HighlightQuote eyebrow="What this section solves">
            &ldquo;Helping users go from &lsquo;I don&apos;t know what to watch&rsquo; to &lsquo;this looks right&rsquo; in a few steps.&rdquo;
          </HighlightQuote>

          <IllustrationBox icon={Compass} label="End-of-page recommendation interaction — tap the phone to walk through the flow">
            <ScreenPhone
              src={[tpSpecialsImg, categoryImg, filterImg][endIdx]}
              alt={['TP Specials — Can\'t find what you\'re looking for', 'Category selection', 'Filter result with Theatre pack'][endIdx]}
              onTap={advanceEnd}
              step={endIdx + 1}
              stepLabel={['TP Specials', 'Category', 'Filter'][endIdx]}
              totalSteps={3}
            />
          </IllustrationBox>

          <P>
            Placing this at the bottom rather than the top was deliberate. Everything above it is built to spark curiosity without asking
            for a decision. This is the one place on the page that finally asks for one — placed only after every lighter, lower-effort
            option had already had its chance.
          </P>
        </Chapter>

        <Ornament />

        {/* 06 — SCALE + LEARNINGS */}
        <Chapter id="scale" number="06 — Designing for scale, not just this page">
          <H2>Every widget was built to <em className="italic" style={{ color: 'var(--terracotta)' }}>outlive this one destination.</em></H2>
          <P>
            None of these components were one-off decorations. Each was built as a modular piece that could be reused elsewhere in the app,
            not stitched together as a single bespoke page.
          </P>

          <div className="reveal grid grid-cols-1 md:grid-cols-3 gap-3 my-7">
            <ModuleCard Icon={Repeat}    title="Reusable across the app" />
            <ModuleCard Icon={Puzzle}    title="Adaptable to other contexts" />
            <ModuleCard Icon={Zap}       title="Faster to iterate on" />
          </div>

          <P>
            And unlike a typical web redesign, this wasn&apos;t about hover states or page transitions. The whole project leaned on real
            interaction patterns — with motion supporting the interaction, never replacing it.
          </P>

          <div className="reveal flex flex-wrap justify-center gap-2 my-6">
            <Chip Icon={MousePointerClick}>Tap</Chip>
            <Chip Icon={Phone}>Shake</Chip>
            <Chip Icon={Hand}>Swipe</Chip>
            <Chip Icon={Play}>Quick previews</Chip>
            <Chip Icon={Film}>Motion-led headers</Chip>
          </div>

          <IllustrationBox icon={Layers} label="Widget library — selected modules from the 10+ built">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <LibraryCard
                Icon={Hand}
                title="Get a Special"
                sub="Swipe-to-reveal interaction"
                images={[special1Img, special2Img]}
                activeIdx={getSpecialIdx}
                onTap={advanceGetSpecial}
              />
              <LibraryCard
                Icon={Gift}
                title="Recharge reward unlocked"
                sub="Tab switch changes the video preview below"
                images={[rechargeRewardImg]}
              />
              <LibraryCard
                Icon={Dices}
                title="Recommendation of the week"
                sub="Shake-to-reveal interaction"
                images={[recc1Img, recc2Img]}
                activeIdx={libReccIdx}
                onTap={advanceLibRecc}
              />
            </div>
          </IllustrationBox>

          <div className="mt-12">
            <InsightBlock Icon={TrendingUp}>
              The shift wasn&apos;t just visual. People went from <strong className="font-semibold" style={{ color: 'var(--ink)' }}>passively
              scrolling</strong> to <strong className="font-semibold" style={{ color: 'var(--ink)' }}>actively tapping, exploring, and discovering</strong> —
              the page stopped being something you scrolled past and became something you did something with.
            </InsightBlock>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mt-8">
            <LearningCard n="01">
              Discovery needs <strong className="font-semibold" style={{ color: 'var(--ink)' }}>momentum</strong>, not just structure. A
              well-organised page can still feel like effort.
            </LearningCard>
            <LearningCard n="02">
              Interactions reduce friction <strong className="font-semibold" style={{ color: 'var(--ink)' }}>when designed with intent</strong>
              — not as decoration on top of a list.
            </LearningCard>
            <LearningCard n="03">
              People engage more when they feel <strong className="font-semibold" style={{ color: 'var(--ink)' }}>in control</strong> of how
              they arrive at a decision.
            </LearningCard>
            <LearningCard n="04">
              <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Playfulness, done right, increases exploration</strong> —
              it doesn&apos;t distract from it.
            </LearningCard>
          </div>

          <ReflectBlock>
            This project pushed me out of structured design thinking. There were no benchmarks, no established patterns to lean on — which
            meant working from first principles. It reinforced something simple: people don&apos;t explore because content exists. They
            explore because it feels worth their time.
          </ReflectBlock>
        </Chapter>

        {/* CLOSING */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center" style={{ borderTop: '1px solid var(--border)' }}>
          <p className="font-display italic leading-[1.5] mb-8 mx-auto max-w-[640px]" style={{ fontSize: 'clamp(20px, 2.2vw, 26px)', color: 'var(--ink-soft)' }}>
            &ldquo;People don&apos;t explore because content exists. They explore because it feels worth their time.&rdquo;
          </p>
          <div className="flex flex-wrap justify-center gap-3.5">
            <a
              href="https://ritikaghosh.framer.website/"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] transition-colors duration-300"
              style={{ background: 'var(--terracotta)', color: '#FAF6EE', border: '1.5px solid var(--terracotta)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#A4501F'; e.currentTarget.style.borderColor = '#A4501F'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--terracotta)'; e.currentTarget.style.borderColor = 'var(--terracotta)'; }}
            >
              View actual prototype <ArrowUpRight size={15} />
            </a>
            <Link
              to="/#work"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] transition-colors duration-300"
              style={{ background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--border)' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ink-soft)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              All case studies <ArrowRight size={15} />
            </Link>
          </div>
        </section>

        {/* Next / back */}
        <div className="max-w-3xl mx-auto px-6 mt-12 pt-10 flex flex-wrap items-center justify-between gap-6" style={{ borderTop: '1px solid var(--border)' }}>
          <Link to="/#work" className="inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase" style={{ color: 'var(--ink)' }}>
            <ArrowLeft size={15} /> <span className="link-underline">Back to all work</span>
          </Link>
          {nextProject && (
            <Link to={`/case/${nextProject.slug}`} className="group inline-flex items-center gap-3 rounded-2xl px-5 py-4 transition-all duration-300" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <span className="text-right">
                <span className="block text-[11px] tracking-[0.18em] uppercase" style={{ color: 'var(--sage)' }}>Next case study</span>
                <span className="block font-display text-[18px]" style={{ color: 'var(--ink)' }}>{nextProject.title}</span>
              </span>
              <ArrowRight size={18} style={{ color: 'var(--terracotta)' }} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}

/* ====== Shared blocks ====== */
function MetaPill({ children, accent = false }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium" style={{ background: accent ? 'var(--terracotta)' : 'var(--surface)', color: accent ? '#FAF6EE' : 'var(--ink-soft)', border: `1px solid ${accent ? 'var(--terracotta)' : 'var(--border)'}` }}>
      {children}
    </span>
  );
}
function Chapter({ id, number, children }) {
  return (
    <section id={id} className="max-w-3xl mx-auto px-6 mb-24 scroll-mt-24">
      {number && <p className="reveal text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: 'var(--ink-soft)' }}>{number}</p>}
      {children}
    </section>
  );
}
function H2({ children }) {
  return (<h2 className="reveal font-display leading-[1.2] mb-6" style={{ fontSize: 'clamp(26px, 3vw, 36px)', color: 'var(--ink)', fontWeight: 600 }}>{children}</h2>);
}
function P({ children }) { return (<p className="reveal text-[15.5px] leading-[1.8] mb-4" style={{ color: 'var(--ink-soft)' }}>{children}</p>); }
function Ornament() {
  return (<div className="reveal my-12 flex justify-center"><span className="text-[18px] tracking-[10px]" style={{ color: 'var(--terracotta)' }}>· · ·</span></div>);
}
function BACard({ variant, label, Icon, children }) {
  const isAfter = variant === 'after';
  return (
    <div
      className="rounded-[14px] p-5 md:p-6 flex items-start gap-4 transition-all duration-300"
      style={{
        background: isAfter ? 'rgba(122,140,110,0.12)' : '#EDE8E0',
        border: `1px solid ${isAfter ? 'rgba(122,140,110,0.45)' : 'var(--border)'}`,
        boxShadow: isAfter ? 'inset 4px 0 0 var(--sage), 0 12px 26px -16px rgba(122,140,110,0.4)' : 'none',
      }}
    >
      <span
        className="shrink-0 w-10 h-10 rounded-[10px] flex items-center justify-center"
        style={{ background: isAfter ? 'rgba(122,140,110,0.22)' : '#F0E8DC', color: isAfter ? 'var(--sage)' : '#8A7A60' }}
      >
        <Icon size={18} />
      </span>
      <div className="flex-1 min-w-0">
        <p
          className="text-[10.5px] tracking-[0.22em] uppercase font-semibold mb-1.5"
          style={{ color: isAfter ? 'var(--sage)' : '#8A7A60' }}
        >
          {label}
        </p>
        <p className="text-[14.5px] leading-[1.6]" style={{ color: 'var(--ink)' }}>{children}</p>
      </div>
    </div>
  );
}
function InsightBlock({ Icon, children }) {
  return (
    <div className="reveal rounded-[14px] p-5 md:p-6 my-7 flex items-start gap-4" style={{ background: '#FAF0E8', border: '1px solid rgba(196,98,45,0.32)', borderLeft: '4px solid var(--terracotta)' }}>
      <span className="shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center" style={{ background: 'rgba(196,98,45,0.18)' }}>
        <Icon size={17} style={{ color: 'var(--terracotta-deep)' }} />
      </span>
      <p className="text-[14.5px] leading-[1.65]" style={{ color: 'var(--ink)' }}>{children}</p>
    </div>
  );
}
function ShiftCard({ tone, label, children }) {
  const isRight = tone === 'right';
  return (
    <div
      className="rounded-[14px] p-5"
      style={{
        background: isRight ? '#FAF0E8' : 'var(--surface)',
        border: `1px solid ${isRight ? 'var(--terracotta)' : 'var(--border)'}`,
        boxShadow: isRight ? '0 10px 22px -14px rgba(196,98,45,0.4)' : 'none',
      }}
    >
      <p className="text-[10.5px] tracking-[0.22em] uppercase font-semibold mb-2" style={{ color: isRight ? 'var(--terracotta-deep)' : '#8A7A60' }}>{label}</p>
      <p className="font-display italic text-[18px] leading-[1.4]" style={{ color: 'var(--ink)' }}>{children}</p>
    </div>
  );
}
function ApproachCard({ accent, label, Icon, title, body }) {
  const isTerra = accent === 'terracotta';
  const main = isTerra ? 'var(--terracotta)' : 'var(--sage)';
  const tint = isTerra ? '#FAF0E8' : 'rgba(122,140,110,0.12)';
  return (
    <div
      className="rounded-[16px] p-6 md:p-7 transition-all duration-300 flex flex-col h-full"
      style={{
        background: tint,
        border: `1px solid ${isTerra ? 'rgba(196,98,45,0.35)' : 'rgba(122,140,110,0.45)'}`,
        boxShadow: `0 16px 32px -20px ${main}55`,
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-[10.5px] tracking-[0.22em] uppercase font-semibold" style={{ color: main }}>{label}</p>
        <span className="w-10 h-10 rounded-[10px] flex items-center justify-center" style={{ background: '#FAF6EE', color: main, border: `1px solid ${main}33` }}>
          <Icon size={18} />
        </span>
      </div>
      <h3 className="font-display text-[22px] md:text-[24px] leading-[1.2] mb-3" style={{ color: 'var(--ink)', fontWeight: 600 }}>
        {title}
      </h3>
      <p className="text-[14px] leading-[1.65]" style={{ color: 'var(--ink-soft)' }}>{body}</p>
    </div>
  );
}
function WidgetBlock({ n, title, Icon, sub, images, activeIdx = 0, onTap }) {
  const src = images && images[activeIdx] ? images[activeIdx] : null;
  const canTap = typeof onTap === 'function' && Array.isArray(images) && images.length > 1;
  return (
    <div className="reveal my-7 rounded-[18px] p-6 md:p-7" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: '#FAF0E8', color: 'var(--terracotta-deep)', border: '1px solid rgba(196,98,45,0.25)' }}>
          <Icon size={18} />
        </span>
        <div className="flex-1">
          <p className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: 'var(--sage)' }}>{n}</p>
          <h3 className="font-display text-[22px] md:text-[24px] leading-tight" style={{ color: 'var(--ink)', fontWeight: 600 }}>{title}</h3>
        </div>
      </div>
      <p className="text-[14px] leading-[1.65] mb-5" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={canTap ? onTap : undefined}
          disabled={!canTap}
          aria-label={canTap ? `${title} — tap to reveal next state` : title}
          className="rounded-[24px] shadow-warm w-full max-w-[280px] overflow-hidden block p-0 transition-transform duration-200 hover:scale-[1.015] active:scale-[0.99]"
          style={{ aspectRatio: '360 / 800', background: '#0F0A14', border: '1.5px solid var(--ink)', cursor: canTap ? 'pointer' : 'default' }}
        >
          {src ? (
            <img
              key={src}
              src={src}
              alt={`${title} screen`}
              className="block w-full h-full object-cover transition-opacity duration-500"
              loading="lazy"
            />
          ) : (
            <div className="h-full w-full flex flex-col items-center justify-center gap-3 text-center px-6" style={{ background: 'linear-gradient(165deg, #1F1620 0%, #0F0A14 100%)' }}>
              <span className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'rgba(196,98,45,0.18)', border: '1.5px solid rgba(196,98,45,0.5)' }}>
                <Icon size={26} style={{ color: '#F2C4A2' }} />
              </span>
              <p className="text-[13px] font-medium" style={{ color: '#FAF6EE' }}>{title}</p>
              <p className="text-[11px] tracking-[0.18em] uppercase" style={{ color: '#9A8A78' }}>360 × 800 placeholder</p>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
function ProtoButton({ children, href }) {
  return (
    <a
      href={href || 'https://ritikaghosh.framer.website/'}
      target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[12.5px] transition-all duration-200"
      style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1.5px solid var(--border)' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--terracotta)'; e.currentTarget.style.color = '#FAF6EE'; e.currentTarget.style.borderColor = 'var(--terracotta)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg)'; e.currentTarget.style.color = 'var(--ink)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <Play size={12} /> {children} <ChevronRight size={13} />
    </a>
  );
}
function IllustrationBox({ icon: Icon, label, children }) {
  return (
    <div className="reveal my-8 rounded-[18px] p-6 md:p-7" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 mb-4 text-[11px] tracking-[0.18em] uppercase font-medium" style={{ color: 'var(--sage)' }}>
        <Icon size={13} /> {label}
      </div>
      {children}
    </div>
  );
}
function ScreenPhone({ src, alt, onTap, step, stepLabel, totalSteps }) {
  const canTap = typeof onTap === 'function';
  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={canTap ? onTap : undefined}
        disabled={!canTap}
        aria-label={canTap ? `${alt} — tap to advance` : alt}
        className="rounded-[24px] shadow-warm w-full max-w-[300px] overflow-hidden block p-0 transition-transform duration-200 hover:scale-[1.015] active:scale-[0.99] relative"
        style={{ aspectRatio: '360 / 800', background: '#0F0A14', border: '1.5px solid var(--ink)', cursor: canTap ? 'pointer' : 'default' }}
      >
        <img key={src} src={src} alt={alt} className="block w-full h-full object-cover" loading="lazy" />
        {canTap && stepLabel && (
          <div
            className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[9.5px] font-semibold tracking-[0.14em] uppercase pointer-events-none"
            style={{ background: 'rgba(15,10,20,0.78)', color: '#FAF6EE', backdropFilter: 'blur(8px)' }}
          >
            Step {step}/{totalSteps} · {stepLabel}
          </div>
        )}
      </button>
      {canTap && (
        <p className="text-[11.5px] mt-3 italic text-center" style={{ color: 'var(--ink-soft)' }}>
          Tap the screen to advance{step === totalSteps && ' · resets in 2 min'}
        </p>
      )}
    </div>
  );
}
function LibraryCard({ Icon, title, sub, images, placeholder, activeIdx = 0, onTap }) {
  const src = !placeholder && images && images[activeIdx] ? images[activeIdx] : null;
  const canTap = typeof onTap === 'function' && Array.isArray(images) && images.length > 1;
  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={canTap ? onTap : undefined}
        disabled={!canTap}
        aria-label={canTap ? `${title} — tap to reveal` : title}
        className="rounded-[20px] shadow-warm w-full max-w-[220px] overflow-hidden block p-0 transition-transform duration-200 hover:scale-[1.015] active:scale-[0.99]"
        style={{ aspectRatio: '360 / 800', background: '#0F0A14', border: '1.5px solid var(--ink)', cursor: canTap ? 'pointer' : 'default' }}
      >
        {src ? (
          <img key={src} src={src} alt={title} className="block w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center gap-3 text-center px-5" style={{ background: 'linear-gradient(165deg, #1F1620 0%, #0F0A14 100%)' }}>
            <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(196,98,45,0.18)', border: '1.5px solid rgba(196,98,45,0.5)' }}>
              <Icon size={22} style={{ color: '#F2C4A2' }} />
            </span>
            <p className="text-[12px] font-medium" style={{ color: '#FAF6EE' }}>{title}</p>
          </div>
        )}
      </button>
      <p className="text-[12.5px] font-medium mt-3 leading-tight text-center" style={{ color: 'var(--ink)' }}>{title}</p>
      <p className="text-[11.5px] leading-snug text-center" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
    </div>
  );
}
function Placeholder({ Icon, title, children }) {
  return (
    <div className="w-full text-center flex flex-col items-center gap-2.5 px-5 py-10 rounded-[12px]" style={{ background: 'linear-gradient(135deg, #F0E8DC 0%, #E8DDD0 100%)', border: '1.5px dashed var(--border)' }}>
      <span className="w-10 h-10 rounded-[10px] flex items-center justify-center" style={{ background: '#F2C4A2' }}>
        <Icon size={18} style={{ color: 'var(--terracotta-deep)' }} />
      </span>
      <span className="font-medium text-[13px]" style={{ color: 'var(--ink)' }}>{title}</span>
      <span className="text-[12px] leading-snug max-w-[320px]" style={{ color: 'var(--ink-soft)' }}>{children}</span>
    </div>
  );
}
function StepCard({ label, children }) {
  return (
    <div className="rounded-[14px] p-5 flex flex-col items-start gap-2 transition-all duration-300" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <p className="text-[10.5px] tracking-[0.22em] uppercase font-semibold" style={{ color: 'var(--terracotta-deep)' }}>{label}</p>
      <p className="text-[15px] leading-[1.5]" style={{ color: 'var(--ink)' }}>{children}</p>
    </div>
  );
}
function HighlightQuote({ eyebrow, children }) {
  return (
    <div
      className="reveal my-10 rounded-[18px] p-7 md:p-9 text-center relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, var(--terracotta) 0%, var(--terracotta-deep) 100%)',
        boxShadow: '0 22px 48px -20px rgba(196,98,45,0.55)',
      }}
    >
      <span className="absolute top-3 left-5 font-display text-[80px] leading-none opacity-30 select-none" style={{ color: '#FAF6EE' }}>&ldquo;</span>
      <span className="absolute bottom-0 right-5 font-display text-[80px] leading-none opacity-30 select-none" style={{ color: '#FAF6EE' }}>&rdquo;</span>
      {eyebrow && (
        <p className="text-[10.5px] tracking-[0.28em] uppercase mb-3 font-semibold relative" style={{ color: '#FADDCC' }}>{eyebrow}</p>
      )}
      <p
        className="font-display italic leading-[1.4] relative"
        style={{ fontSize: 'clamp(20px, 2.6vw, 26px)', color: '#FAF6EE', fontWeight: 500 }}
      >
        {children}
      </p>
    </div>
  );
}
function ModuleCard({ Icon, title }) {
  return (
    <div className="rounded-[12px] p-4 flex items-center gap-3 transition-all duration-300" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <span className="w-10 h-10 rounded-[10px] flex items-center justify-center" style={{ background: '#FAF0E8', color: 'var(--terracotta-deep)' }}>
        <Icon size={18} />
      </span>
      <p className="text-[13.5px] font-medium leading-tight" style={{ color: 'var(--ink)' }}>{title}</p>
    </div>
  );
}
function Chip({ Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12.5px] font-medium" style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--border)' }}>
      <Icon size={13} style={{ color: 'var(--terracotta)' }} /> {children}
    </span>
  );
}
function LearningCard({ n, children }) {
  return (
    <div className="rounded-[14px] p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <p className="font-display text-[18px] mb-2" style={{ color: 'var(--terracotta)', fontWeight: 600 }}>{n}</p>
      <p className="text-[14px] leading-[1.65]" style={{ color: 'var(--ink-soft)' }}>{children}</p>
    </div>
  );
}
function ReflectBlock({ children }) {
  return (
    <div className="reveal relative mt-14 mb-4">
      {/* huge translucent display word behind */}
      <span
        aria-hidden="true"
        className="hidden md:block absolute -top-10 -left-2 font-display italic select-none pointer-events-none"
        style={{
          fontSize: 'clamp(120px, 15vw, 190px)',
          lineHeight: 1,
          color: 'var(--ochre)',
          opacity: 0.16,
          fontWeight: 700,
          letterSpacing: '-0.04em',
        }}
      >
        Reflection.
      </span>

      <div
        className="relative rounded-[22px] p-8 md:p-12 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, var(--ink) 0%, #2A1814 100%)',
          color: '#FAF6EE',
          boxShadow: '0 28px 56px -22px rgba(28,18,14,0.5)',
        }}
      >
        {/* corner ornament */}
        <span
          aria-hidden="true"
          className="absolute top-5 right-6 text-[20px] tracking-[10px]"
          style={{ color: 'var(--ochre)' }}
        >
          · · ·
        </span>
        <span
          aria-hidden="true"
          className="absolute -top-2 left-6 font-display select-none pointer-events-none"
          style={{ fontSize: 110, lineHeight: 1, color: 'var(--ochre)', opacity: 0.35 }}
        >
          &ldquo;
        </span>

        <p className="text-[10.5px] tracking-[0.32em] uppercase mb-4 font-semibold relative" style={{ color: 'var(--ochre)' }}>
          Reflection
        </p>

        <p
          className="font-display italic leading-[1.35] relative"
          style={{ fontSize: 'clamp(22px, 2.8vw, 30px)', color: '#FAF6EE', fontWeight: 500 }}
        >
          {children}
        </p>

        <div className="mt-7 pt-5 flex items-center gap-3" style={{ borderTop: '1px solid rgba(218,196,160,0.2)' }}>
          <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--ochre)', color: 'var(--ink)' }}>
            <ArrowUpRight size={16} />
          </span>
          <span className="text-[11.5px] tracking-[0.18em] uppercase font-semibold" style={{ color: '#D8C8B4' }}>
            What this project changed in me
          </span>
        </div>
      </div>
    </div>
  );
}
